/**
 * comments.ts — reviewer comments on an article.
 *
 * A comment points at a passage by quoting it (W3C Web Annotation "TextQuoteSelector": the exact text plus a little
 * text either side). Nothing is written into the article, so the Markdown stays clean, and the passage is found again
 * after the author edits. When the passage was rewritten away, the comment is "detached": it stays in the list.
 */
import { z } from "zod";

export const COMMENT_CONTEXT_CHARS = 32;
export const COMMENT_NOTE_MAX = 1000;
export const COMMENT_QUOTE_MAX = 600;

export const contentCommentSchema = z.object({
  id: z.string(),
  contentId: z.string(),
  quote: z.string(),
  prefix: z.string(),
  suffix: z.string(),
  note: z.string(),
  createdBy: z.string().nullable(),
  createdByName: z.string(),
  createdAt: z.string(),
  resolvedAt: z.string().nullable(),
  resolvedByName: z.string().nullable(),
});
export type ContentComment = z.infer<typeof contentCommentSchema>;

export const createCommentSchema = z.object({
  quote: z.string().trim().min(1, "Select some text first.").max(COMMENT_QUOTE_MAX, "Select a shorter passage."),
  prefix: z.string().max(COMMENT_CONTEXT_CHARS * 2).default(""),
  suffix: z.string().max(COMMENT_CONTEXT_CHARS * 2).default(""),
  note: z.string().trim().min(1, "Write what needs to change.").max(COMMENT_NOTE_MAX, "Keep the note shorter."),
});
export type CreateCommentInput = z.infer<typeof createCommentSchema>;

export const resolveCommentSchema = z.object({ resolved: z.boolean() });

export interface CommentAnchor {
  quote: string;
  prefix: string;
  suffix: string;
}

/** The quote with its surrounding text, taken from the text the reviewer selected in. */
export function anchorFor(text: string, from: number, to: number): CommentAnchor {
  return {
    quote: text.slice(from, to),
    prefix: text.slice(Math.max(0, from - COMMENT_CONTEXT_CHARS), from),
    suffix: text.slice(to, to + COMMENT_CONTEXT_CHARS),
  };
}

const commonSuffix = (a: string, b: string) => {
  let n = 0;
  while (n < a.length && n < b.length && a[a.length - 1 - n] === b[b.length - 1 - n]) n++;
  return n;
};
const commonPrefix = (a: string, b: string) => {
  let n = 0;
  while (n < a.length && n < b.length && a[n] === b[n]) n++;
  return n;
};

/**
 * Where the quoted passage is now, as character offsets into `text`, or null when it is gone.
 * With several matches, the one whose surrounding text agrees best wins.
 */
export function locateQuote(text: string, anchor: CommentAnchor): { from: number; to: number } | null {
  const { quote, prefix, suffix } = anchor;
  if (!quote) return null;
  let best: { from: number; score: number } | null = null;
  for (let at = text.indexOf(quote); at !== -1; at = text.indexOf(quote, at + 1)) {
    const score = commonSuffix(text.slice(0, at), prefix) + commonPrefix(text.slice(at + quote.length), suffix);
    if (!best || score > best.score) best = { from: at, score };
  }
  return best ? { from: best.from, to: best.from + quote.length } : null;
}

/** The line the author is notified with: the manager's own words, or a count when they wrote none. */
export function sendBackReason(note: string, openComments: number): string {
  const n = note.trim();
  const count = openComments === 1 ? "1 comment" : `${openComments} comments`;
  if (n && openComments > 0) return `${n} (${count} in the article)`;
  if (n) return n;
  return openComments > 0 ? `${count} to fix in the article.` : "Sent back for changes.";
}
